"use client"

import { fill } from "../../../lib/fill"
import { useTr } from "../../../lib/use-tr"
import { Check, ExternalLink, Hourglass, Lock, X } from "lucide-react"

import { isHttpUrl } from "@/lib/safe-url"
import { cn } from "@/lib/utils"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

import { EXTRACTIONS_T } from "@/app/autoleads/components/extractions/translations"

import type { Lead } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type { ExtractionSegment } from "@/nango/types/apps.autoleads/autoleads/LeadExtraction"

const SEGMENT_STYLE: Record<
  ExtractionSegment,
  { Icon: typeof Check; className: string }
> = {
  sent: { Icon: Lock, className: "bg-muted text-foreground" },
  ready: { Icon: Check, className: "bg-primary/10 text-primary" },
  excluded: { Icon: X, className: "bg-muted text-muted-foreground" },
  pending: { Icon: Hourglass, className: "bg-muted text-muted-foreground" },
}

// Where the lead stands against its campaign's extractions, as one icon: sent
// (locked), kept, left out or awaiting the AI. The words sit in the tooltip and
// the AI review's reasons one click away. With no extraction attached a lead
// that is simply pushed shows nothing.
export function LeadExtractionBadge({
  lead,
  extractionNames,
  active = false,
}: {
  lead: Pick<Lead, "extraction_segment" | "extraction_verdicts">
  extractionNames?: Map<number, string>
  // The campaign attaches extractions: every lead shows where it stands.
  active?: boolean
}) {
  const tr = useTr(EXTRACTIONS_T)
  const verdicts = lead.extraction_verdicts ?? []
  // Public reads (samples, case studies) leave the segment out: same as ready.
  const segment = lead.extraction_segment ?? "ready"
  const quiet = segment === "ready" || segment === "sent"
  if (!active && quiet && verdicts.length === 0) return null
  const { Icon, className } = SEGMENT_STYLE[segment]
  const label =
    segment === "sent"
      ? tr("segSent")
      : segment === "ready"
        ? tr("segReady")
        : segment === "excluded"
          ? tr("segExcluded")
          : fill(tr("segPendingHint"), { n: 1 })
  const hint =
    segment === "sent"
      ? tr("segSentHint")
      : segment === "ready"
        ? tr("segReadyHint")
        : segment === "excluded"
          ? tr("segExcludedHint")
          : null
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={label}
              className={cn(
                "focus-visible:ring-ring inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full focus-visible:ring-2 focus-visible:outline-none",
                className,
              )}
            >
              <Icon className="size-3" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-80 space-y-2 text-left text-xs"
      >
        <p className="font-medium">{label}</p>
        {hint ? <p className="text-muted-foreground">{hint}</p> : null}
        {verdicts.length > 0 ? (
          <ul className="space-y-2 border-t pt-2">
            {verdicts.map((verdict) => {
              const name = extractionNames?.get(verdict.extraction_id)
              return (
                <li key={verdict.extraction_id} className="space-y-0.5">
                  <p className="flex items-center gap-1 font-medium">
                    {verdict.passed ? (
                      <Check className="text-primary size-3 shrink-0" />
                    ) : (
                      <X className="text-destructive size-3 shrink-0" />
                    )}
                    <span className="min-w-0 break-words">
                      {name ? `${name}: ` : `${tr("verdictsTitle")}: `}
                      {verdict.passed
                        ? tr("verdictPassed")
                        : tr("verdictFailed")}
                    </span>
                  </p>
                  {verdict.reason ? (
                    <p className="text-muted-foreground break-words">
                      {verdict.reason}
                    </p>
                  ) : null}
                  {verdict.source_url && isHttpUrl(verdict.source_url) ? (
                    <a
                      href={verdict.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-primary inline-flex items-center gap-0.5 hover:underline"
                    >
                      <ExternalLink className="size-3 shrink-0" />
                      {tr("verdictSource")}
                    </a>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
